import { useEffect, useState } from "react"
import { useRouter } from "next/router"
import { useMutation } from "@tanstack/react-query"
import { useAuth } from "@/providers/authContext"
import ChatSidebar from "../AIAgent/ChatSidebar"
import CommonNavbar from "../common/CommonNavbar"
import MobileNavigation from "../common/MobileNavigation"
import ModalsPortal from "../ModalsPortal"
import { menuCommonPages } from "./CommonLayout"
import { withLazyProviders } from "./with-lazy-providers"
import client from "@/api/client"
import LoadingLogo from "../LoadingLogo"

const AIAgentLayout = ({ children, hiddenSidebar }: { children: React.ReactNode; hiddenSidebar?: boolean }) => {
  const { bearerToken } = useAuth()
  const router = useRouter()
  const [isAgentVerified, setIsAgentVerified] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const { mutate: getAgent } = useMutation({
    mutationFn: () =>
      client.api.aiAgentControllerGetAgent({
        headers: {
          Authorization: `Bearer ${bearerToken}`,
        },
      }),
    onSuccess: () => {
      setIsAgentVerified(true)
      setIsLoading(false)
    },
    onError: () => {
      router.replace("/explore")
    },
  })

  useEffect(() => {
    // Wait for the bearer token to be available
    if (bearerToken) {
      getAgent()
    }
  }, [getAgent, bearerToken])

  if (isLoading) {
    return (
      <main className="flex items-center justify-center w-full min-h-screen dark:bg-black dark:text-atherGray-0">
        <LoadingLogo />
      </main>
    )
  }

  if (!isAgentVerified) {
    return null // Will not render as we're redirecting
  }

  return (
    <main id="layout" className="relative block w-full dark:bg-black dark:text-atherGray-0">
      <div id="layout-inside" className="relative flex flex-col w-full h-dvh overflow-hidden">
        <CommonNavbar navItems={menuCommonPages} />
        <div className={"flex-1 flex pb-[5.15rem] md:pb-0 overflow-hidden"}>
          {!hiddenSidebar && <ChatSidebar />}
          <div className="relative flex flex-col items-center flex-1 w-full overflow-hidden md:px-6 transform-gpu">
            <div className="flex flex-col max-w-4xl 3xl:max-w-5xl 4xl:max-w-6xl mx-auto w-full relative flex-1 overflow-hidden">
              {children}
            </div>
          </div>
          <MobileNavigation />
        </div>
      </div>
      <ModalsPortal />
    </main>
  )
}

export default withLazyProviders(AIAgentLayout)
